import React from 'react';

const formatDate = (d) => {
  if (!d) return '--';
  try {
    const date = new Date(d);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return d;
  }
};

/**
 * BidPrepTab - Wired to Phase 4 data (evaluation criteria, mandatory docs, submission requirements, key dates)
 */
const BidPrepTab = ({ tender, phase4Data }) => {
  const criteria = phase4Data?.evaluation_criteria || [];
  const docs = phase4Data?.mandatory_documents || [];
  const submission = phase4Data?.submission_requirements || {};
  const keyDates = phase4Data?.key_dates || [];

  if (!phase4Data) {
    return (
      <div className="bidprep-tab">
        <div className="no-assessment-card">
          <div className="no-assessment-icon">📝</div>
          <h4>No Bid Prep Data Yet</h4>
          <p>Run the analysis to extract evaluation criteria, mandatory documents, and submission requirements.</p>
        </div>
      </div>
    );
  }

  const maxWeight = criteria.length > 0
    ? Math.max(...criteria.map(c => parseFloat(c.weight_pct) || 0))
    : 100;

  return (
    <div className="bidprep-tab">
      {/* Evaluation Criteria */}
      {criteria.length > 0 && (
        <div className="summary-section">
          <h4 className="summary-section-title">Evaluation Criteria</h4>
          <table className="products-table">
            <thead>
              <tr>
                <th>Criterion</th>
                <th>Weight</th>
                <th style={{ width: '30%' }}>Visual</th>
                <th>Section</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {criteria.map((c, i) => {
                const weight = parseFloat(c.weight_pct) || 0;
                const barPct = maxWeight > 0 ? (weight / maxWeight) * 100 : 0;
                return (
                  <tr key={i}>
                    <td style={{ fontWeight: 600 }}>{c.criterion}</td>
                    <td style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {weight > 0 ? `${weight}%` : '--'}
                    </td>
                    <td>
                      <div className="eval-bar-track">
                        <div
                          className="eval-bar-fill"
                          style={{ width: `${barPct}%`, background: 'linear-gradient(90deg, #2563eb, #6366f1)' }}
                        ></div>
                      </div>
                    </td>
                    <td>
                      {c.section_ref && (
                        <span className="heuristic-chip">{c.section_ref}</span>
                      )}
                    </td>
                    <td style={{ fontSize: 12, color: '#6b7280' }}>{c.details}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Submission Checklist */}
      {docs.length > 0 && (
        <div className="summary-section">
          <h4 className="summary-section-title">Mandatory Documents</h4>
          <table className="products-table">
            <thead>
              <tr>
                <th>Document</th>
                <th>Form ID</th>
                <th>Section</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 600 }}>{d.document}</td>
                  <td>
                    {d.form_id ? (
                      <span className="heuristic-chip">{d.form_id}</span>
                    ) : '--'}
                  </td>
                  <td>
                    {d.section_ref ? (
                      <span className="heuristic-chip">{d.section_ref}</span>
                    ) : '--'}
                  </td>
                  <td style={{ fontSize: 12, color: '#6b7280' }}>{d.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Submission Requirements */}
      {(submission.deadline || submission.method || submission.format) && (
        <div className="summary-section">
          <h4 className="summary-section-title">Submission Requirements</h4>
          <div className="decision-grid">
            {submission.deadline && (
              <div className="decision-card amber">
                <div className="decision-header">
                  <span className="decision-type">Deadline</span>
                </div>
                <p className="decision-reason" style={{ fontWeight: 600 }}>{formatDate(submission.deadline)}</p>
              </div>
            )}
            {submission.method && (
              <div className="decision-card green">
                <div className="decision-header">
                  <span className="decision-type">Method</span>
                </div>
                <p className="decision-reason">{submission.method}</p>
              </div>
            )}
            {submission.format && (
              <div className="decision-card green">
                <div className="decision-header">
                  <span className="decision-type">Format</span>
                </div>
                <p className="decision-reason">{submission.format}</p>
              </div>
            )}
            {submission.page_limit && (
              <div className="decision-card green">
                <div className="decision-header">
                  <span className="decision-type">Page Limit</span>
                </div>
                <p className="decision-reason">{submission.page_limit} pages</p>
              </div>
            )}
            {submission.language && (
              <div className="decision-card green">
                <div className="decision-header">
                  <span className="decision-type">Language</span>
                </div>
                <p className="decision-reason">{submission.language}</p>
              </div>
            )}
            {submission.address && (
              <div className="decision-card green">
                <div className="decision-header">
                  <span className="decision-type">Address</span>
                </div>
                <p className="decision-reason" style={{ fontSize: 12, wordBreak: 'break-word' }}>{submission.address}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Key Dates Timeline */}
      {keyDates.length > 0 && (
        <div className="summary-section">
          <h4 className="summary-section-title">Key Dates</h4>
          <div className="timeline-grid">
            {keyDates.map((kd, i) => {
              const isUpcoming = kd.date && new Date(kd.date) > new Date();
              const isPast = kd.date && new Date(kd.date) < new Date();
              return (
                <div key={i} className={`timeline-item ${isPast ? '' : 'urgent'}`}>
                  <span className="timeline-label">{kd.event}</span>
                  <span className="timeline-value">{formatDate(kd.date)}</span>
                  {kd.section_ref && (
                    <span style={{ fontSize: 11, color: '#6b7280' }}>Section {kd.section_ref}</span>
                  )}
                  {isPast && (
                    <span className="days-badge warning">Past</span>
                  )}
                  {isUpcoming && (
                    <span className="days-badge ok">Upcoming</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Confidence */}
      {phase4Data.confidence && (
        <div className="summary-footer">
          <span className="footer-item">
            Confidence: <strong>{phase4Data.confidence}</strong>
          </span>
        </div>
      )}
    </div>
  );
};

export default BidPrepTab;
